import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { PendingVenue, RequestChangesInput } from "./types";
import { PENDING_VENUES_QUERY_KEY } from "./use-pending-venues";

interface RequestChangesParams {
  readonly venueId: string;
  readonly input: RequestChangesInput;
}

async function requestChanges({ venueId, input }: RequestChangesParams): Promise<unknown> {
  return httpClient(`/api/admin/venues/${venueId}/request-changes`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useRequestChanges() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestChanges,
    onMutate: async ({ venueId }) => {
      await queryClient.cancelQueries({ queryKey: PENDING_VENUES_QUERY_KEY });

      const previous = queryClient.getQueryData<readonly PendingVenue[]>(PENDING_VENUES_QUERY_KEY);

      // See comment in use-approve-venue.ts re: undefined updater.
      queryClient.setQueryData<readonly PendingVenue[]>(PENDING_VENUES_QUERY_KEY, (old) =>
        old?.filter((v) => v.id !== venueId),
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PENDING_VENUES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PENDING_VENUES_QUERY_KEY });
    },
  });
}
